import React, { useState, useMemo } from "react";
import { Badge, Popover } from "antd";
import { HardDrive, Settings2, Trash2, BellOff } from "lucide-react";
import {
  Bell,
  Ticket as TicketIcon,
  FileCheck2,
  UserCheck,
  Users,
  Check,
  X,
} from "./icons";
import { useNavigate } from "react-router-dom";
import useNotifications from "../hooks/useNotifications";
import usePushNotificaciones from "../hooks/usePushNotificaciones";
import { useAuth } from "../contexts/AuthContext";

// Icono + color por tipo de notificación (tipos reales que usa el backend:
// ticket, activo, acta, asignacion, pazysalvo, sistema, usuario). No hay un
// "usuario que la origina" en el modelo (son notificaciones del sistema, no
// una interacción social), así que en vez de un avatar de persona se usa un
// ícono de la categoría, coherente con el dato que sí existe.
const TIPO_CONFIG = {
  ticket: { icon: TicketIcon, bg: "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400" },
  activo: { icon: HardDrive, bg: "bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400" },
  acta: { icon: FileCheck2, bg: "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400" },
  asignacion: { icon: UserCheck, bg: "bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400" },
  pazysalvo: { icon: FileCheck2, bg: "bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-400" },
  usuario: { icon: Users, bg: "bg-pink-100 text-pink-600 dark:bg-pink-900/40 dark:text-pink-400" },
  sistema: { icon: Settings2, bg: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300" },
};

function getTipoConfig(tipo) {
  return TIPO_CONFIG[tipo] || TIPO_CONFIG.sistema;
}

// Tiempo relativo simple, mismo criterio usado en el resto de la app
// (InvGateTicketCard) -- sin librerias nuevas.
function formatTimeAgo(dateString) {
  if (!dateString) return "";
  const diffMs = new Date() - new Date(dateString);
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "ahora";
  if (diffMins < 60) return `hace ${diffMins} min`;
  if (diffHours < 24) return `hace ${diffHours} h`;
  if (diffDays < 7) return `hace ${diffDays} d`;
  return new Date(dateString).toLocaleDateString("es-CL", { day: "numeric", month: "short" });
}

function NotificationItem({ notification, onOpen, onDelete }) {
  const { icon: Icon, bg } = getTipoConfig(notification.tipo);

  return (
    <div
      onClick={() => onOpen(notification)}
      className="group flex gap-3 px-4 py-3.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
    >
      <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${bg}`}>
        <Icon className="w-4.5 h-4.5" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <span className={`text-sm leading-snug ${!notification.isRead ? 'font-semibold text-gray-900 dark:text-white' : 'font-medium text-gray-700 dark:text-gray-300'}`}>
            {notification.titulo}
          </span>
          {!notification.isRead && (
            <span className="mt-1.5 size-1.5 rounded-full bg-emerald-500 shrink-0" />
          )}
        </div>

        {notification.mensaje && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-2">
            {notification.mensaje}
          </p>
        )}

        <div className="mt-1.5 flex items-center justify-between">
          <span className="text-[11px] text-gray-400 dark:text-gray-500">
            {formatTimeAgo(notification.createdAt)}
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(notification.id); }}
            className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 dark:text-gray-600 dark:hover:text-red-400 transition-all p-0.5"
            title="Eliminar"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function NotificationBell() {
  const push = usePushNotificaciones();
  const { items, unread, markAllRead, markAsRead, deleteNotification, deleteAll } = useNotifications();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("all"); // 'all' | 'unread'

  const unreadCount = items.filter(n => !n.isRead).length;
  const filteredItems = useMemo(
    () => (activeTab === "unread" ? items.filter(n => !n.isRead) : items),
    [items, activeTab]
  );

  const handleNotificationClick = (notification) => {
    let target = notification.ruta || '/dashboard';
    // Regla: los usuarios (role 'usuario') solo pueden ir a sus rutas de usuario
    const role = user?.role;
    if (role === 'usuario') {
      if (target.startsWith('/gestion-actas')) {
        target = '/actas';
      } else if (target.startsWith('/tickets/')) {
        target = '/mis-tickets';
      } else if (target.startsWith('/activos/')) {
        target = '/mis-activos';
      }
    }
    navigate(target);
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
  };

  const content = (
    <div className="w-80 sm:w-96 lg:w-[28rem] max-w-[calc(100vw-32px)] bg-white dark:bg-gray-850 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-3">
        <h3 className="text-sm font-bold text-gray-900 dark:text-white tracking-tight">Tus notificaciones</h3>
        <div className="flex items-center gap-1">
          {unreadCount > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); markAllRead(); }}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title="Marcar todas como leídas"
            >
              <Check className="w-4 h-4" />
            </button>
          )}
          {items.length > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); deleteAll(); }}
              className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title="Borrar todas"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Avisos del navegador (push): llegan aunque el portal esté cerrado */}
      {push.estado === 'desactivado' && (
        <div className="mx-4 mb-3 flex items-center justify-between gap-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 px-3 py-2">
          <p className="text-xs text-blue-800 dark:text-blue-200">Recibe estos avisos también en el navegador, aunque no tengas el portal abierto.</p>
          <button type="button" disabled={push.trabajando} onClick={(e) => { e.stopPropagation(); push.activar(); }}
            className="shrink-0 px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
            {push.trabajando ? 'Activando…' : 'Activar'}
          </button>
        </div>
      )}
      {push.estado === 'bloqueado' && (
        <p className="mx-4 mb-3 text-xs text-amber-600">Los avisos del navegador están bloqueados: habilítalos desde el candado junto a la dirección y recarga la página.</p>
      )}
      {push.estado === 'activado' && (
        <div className="mx-4 mb-3 flex items-center justify-between gap-3 text-xs text-gray-400">
          <span>Avisos del navegador activados en este equipo</span>
          <button type="button" onClick={(e) => { e.stopPropagation(); push.desactivar(); }} className="underline hover:text-gray-600">Desactivar</button>
        </div>
      )}

      {/* Tabs */}
      <div className="px-4 flex items-center gap-1.5 border-b border-gray-100 dark:border-gray-800 pb-3">
        <button
          onClick={() => setActiveTab('all')}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
            activeTab === 'all'
              ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
              : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
          }`}
        >
          Todas
          <span className="text-[10px] opacity-70">{items.length}</span>
        </button>
        <button
          onClick={() => setActiveTab('unread')}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
            activeTab === 'unread'
              ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
              : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
          }`}
        >
          No leídas
          <span className="text-[10px] opacity-70">{unreadCount}</span>
        </button>
      </div>

      {/* Lista */}
      {filteredItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2.5 py-12 text-center px-4">
          <div className="rounded-full bg-gray-100 dark:bg-gray-800 p-3.5">
            <BellOff className="w-5 h-5 text-gray-400 dark:text-gray-500" />
          </div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
            {activeTab === 'unread' ? 'No tienes notificaciones sin leer.' : 'No hay notificaciones.'}
          </p>
        </div>
      ) : (
        <div className="max-h-[26rem] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
          {filteredItems.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onOpen={handleNotificationClick}
              onDelete={deleteNotification}
            />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      content={content}
      overlayClassName="notification-popover"
      styles={{ body: { padding: 0 } }}
      overlayStyle={{
        maxWidth: 'calc(100vw - 32px)',
        width: 'auto'
      }}
    >
      <Badge count={unread} size="small" offset={[-2, 2]}>
        <button className="p-3 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-700 dark:text-gray-200">
          <Bell size={22} />
        </button>
      </Badge>
    </Popover>
  );
}
